/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  FormControl,
  InputLabel,
  Pagination,
  createTheme,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { HighlightOff as DeleteIcon } from '@mui/icons-material';
import { ColumnConfig, ActionType, GroupConfig, TableRowData } from '../../types';
import styles from './CustomTableComponent.module.css';
import { ConfirmationModal } from '../confirmationModal';
import StatusChip from './statusChip';
import NoVisitHistory from '../../assets/SVG/NoVisitHistory.svg';

interface PaginationMeta {
  limit: number;
  offset: number;
  total_count: number;
  next?: string;
  previous?: string;
}

interface EditableTableProps {
  columns: ColumnConfig[];
  data: TableRowData[];
  actionType: ActionType;
  onAction?: (id: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (id: number, field: string, value: any) => void;
  groupConfig?: GroupConfig[];
  dataSize?: number;
  onRowClick?: (id: number) => void;
  onPageChange?: (page: number) => void;
  currentPageoffset?: number; 
  getAllRowData?: (row: TableRowData) => void; 
  noDataText?:string;
  isAppointmentFiltered?: boolean;
  paginationMeta?: PaginationMeta;
}

const EditableTable: React.FC<EditableTableProps> = ({
  // ...existing code...
  columns,
  data,
  actionType,
  onChange,
  onAction,
  groupConfig,
  dataSize,
  onRowClick,
  getAllRowData,
  onPageChange ,
  currentPageoffset = 1,
  noDataText,
  isAppointmentFiltered,
  paginationMeta
}) => {
  const { t } = useTranslation('general');
  
  const theme = createTheme({ 
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '10px', 
          },
        },
      },
    },
  });

  const rowsPerPage = paginationMeta?.limit ?? 10; // Number of rows per page from API or default
  const [page, setPage] = useState<number>(Math.max(currentPageoffset - 1, 0));
  const [tableData, setTableData] = useState<TableRowData[]>(data);
  const [openModal, setOpenModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(Math.max(currentPageoffset - 1, 0));

  // Use API total_count for server-side pagination, or dataSize/data.length for client-side
  const effectiveTotalCount = paginationMeta?.total_count ?? dataSize ?? data.length;
  // When filtering client-side, use filtered data length for display but hide pagination
  const totalPages = isAppointmentFiltered ? 0 : Math.ceil(effectiveTotalCount / rowsPerPage);

  const handleChangePage = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    const adjustedPage = newPage - 1; // Adjust page for zero-based index
    setPage(adjustedPage);
    setCurrentPage(adjustedPage);
    onPageChange?.(newPage); 

  };

    
  const handleInputChange = (
    id: number,
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any
  ) => {
    setTableData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
    onChange?.(id, field, value);
  };

  const renderCell = (
    row: TableRowData,
    column: ColumnConfig
  ) => {
    const value = row[column.field];

    if (!column.editable) {
      if (column.type === 'text' || column.type === 'int') {
        if (column.showLabel && !column.hideLabel) {
          return <span>{column.headerName} : {value}</span>;
        } else {
          return <div className={styles.tb_main_text}>{value}</div>;
        }
      }
      if (column.type === 'component' && React.isValidElement(value)) {
        return value;
      }
      if (column.showLabel  && !column.hideLabel) {
        return <span>{column.headerName} : {value}</span>;
      }
      else {
        return <div className={styles.tb_main_text}>{value}</div>;
      }
    }

    const renderWithLabel = (inputElement: React.ReactNode) => (
      <div className={styles.tb_main_text}>
        {inputElement}
      </div>
    );


    switch (column.type) {
    case 'text':
    case 'int':
      return renderWithLabel(
        <TextField
          sx={{ borderRadius: '12px', '& .MuiOutlinedInput-root': { borderRadius: '12px' } , }}
          variant="outlined"
          type={column.type === 'int' ? 'number' : 'text'}
          value={value}
          label={column.hideLabel ? undefined : column.headerName}
          onChange={(e) =>
            handleInputChange(row.id, column.field, e.target.value)
          }
        />
      );
    case 'select':
      return renderWithLabel(
        <FormControl>
          {/* Only render InputLabel if hideLabel is false */}
          {!column.hideLabel && (
            <InputLabel id={`select-${column.field}`} shrink={true}>
              {column.headerName}
            </InputLabel>
          )}
          <Select
            sx={{
              borderRadius: '12px',
              minWidth: '3rem',
              maxWidth:'9rem',
              '& .MuiOutlinedInput-root': { borderRadius: '12px' }
            }}
            value={value}
            onChange={(e) =>
              handleInputChange(row.id, column.field, e.target.value)
            }
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: '20rem',
                  overflowY: 'auto',
                },
              },
            }}
            label={column.hideLabel ? undefined : column.headerName}
          >
            {column.options?.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
      
    case 'date':
      return renderWithLabel(
        <TextField
          className={styles.tb_main_text}
          sx={{ borderRadius: '12px', '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          label={column.hideLabel ? undefined : column.headerName}
          variant="outlined"
          type="date"
          value={value}
          onChange={(e) =>
            handleInputChange(row.id, column.field, e.target.value)
          }
        />
      ); 
    
    case 'chip':       
      if (column.field === 'status') {
        return <StatusChip value={value} />; 
      }
      return null;
    case 'component':
      return renderWithLabel(React.isValidElement(value) ? value : null);
    default:
      return null;
    }
  };

  const renderActionCell = (rowId: number) => {
    if (actionType === 'select') {
      return <Checkbox />;
    }
    if (actionType === 'delete') {
      return (
        <TableCell sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            sx={{ color: '#CD1313', border: 'none' }}
            onClick={() => {
              setDeleteId(rowId); 
              setOpenModal(true); 
            }}
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      );
    }
    return null;
  };
  const handleConfirmDelete = () => {
    if (deleteId !== null) {
      onAction?.(deleteId); 
      setDeleteId(null); 
      setOpenModal(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false); 
    setDeleteId(null); 
  };

  const renderGroupedCells = (
    row: TableRowData,
    group: GroupConfig
  ) => {

    const groupedContent = group.fields.map((field) =>
      renderCell(row, columns.find((col) => col.field === field)!)
    );

    if (group.type === 'row') {
      return (
        <TableCell colSpan={group.fields.length} >
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {groupedContent.map((content, index) => (
              <span key={index}>{content}</span>
            ))}
          </div>
        </TableCell>
      );
    }

    if (group.type === 'column') {
      return (
        <TableCell sx={{ width: 'auto', maxWidth: '150px' }}>
          <div style={{ display: 'flex', flexDirection: 'column',  justifyContent: 'space-evenly', minHeight: '50px', padding: '0px'}}>
            {groupedContent.map((content, index) => (
              <div key={index}>{content}</div>
            ))}
          </div>
        </TableCell>
      );
    }

    return null;
  };
  useEffect(() => {
    setTableData(data);
  }, [data]);

  useEffect(() => {
    const zeroIndexedPage = Math.max(currentPageoffset - 1, 0);
    const maxPageIndex = totalPages > 0 ? totalPages - 1 : 0;
    const clampedPage = Math.min(zeroIndexedPage, maxPageIndex);

    setPage((prev) => (prev === clampedPage ? prev : clampedPage));
    setCurrentPage((prev) => (prev === clampedPage ? prev : clampedPage));

    if (zeroIndexedPage !== clampedPage && totalPages > 0) {
      onPageChange?.(clampedPage + 1);
    }
    // eslint-disable-next-line
  }, [currentPageoffset, totalPages]);



  return (
    <ThemeProvider theme={theme}>
      <div>
        <TableContainer>
          <Table className={styles.custom_table}>
            <TableHead>
              <TableRow className={styles.custom_table_head}>
                {groupConfig ? (
                  groupConfig.map((group, index) => (
                    <TableCell key={index}>
                      {group.defaultHeaderGroupname? group.defaultHeaderGroupname: group.fields.map(field => columns.find(col => col.field === field)?.headerName).join(' | ')}
                    </TableCell>
                  ))
                ) : (
                  columns.map((column) => (
                    <TableCell key={column.field}>{column.headerName}</TableCell>
                  ))
                )}                
                {actionType !== 'none' && <TableCell sx={{ width: '20px' }}></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (actionType !== 'none' ? 1 : 0) || 1}
                    align="center"
                    sx={{
                      height: '320px',
                      padding: 0,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        gap: '1rem',
                      }}
                    >
                      <img
                        src={NoVisitHistory}
                        alt={t('NoDataFound')}
                        style={{
                          maxWidth: '400px',
                          width: '100%',
                          height: '450px',
                        }}
                      />
                      <span style={{}}>
                        <Typography component="span" sx={{ fontSize: '1rem', mb: 2}}>
                          {noDataText ? noDataText : t('NoDataFound')}
                        </Typography>
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                tableData.map((row) => (
                  <TableRow
                    key={row.id}
                    className={styles.custom_table_body}
                    onClick={() => {
                      onRowClick?.(row.id);
                      if (getAllRowData) {
                        getAllRowData(row);
                      }
                    }}
                  >
                    {groupConfig ? (
                      groupConfig.map((group, index) => (
                        <React.Fragment key={index}>
                          {renderGroupedCells(row, group)}
                        </React.Fragment>
                      ))
                    ) : (
                      columns.map((column) => (
                        <TableCell key={column.field}>{renderCell(row, column)}</TableCell>
                      ))
                    )}
                    {actionType !== 'none' && (
                      <TableCell>
                        {row.status === 'Active' && renderActionCell(row.id)}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}


            </TableBody>
          </Table>
        </TableContainer>



        {/* Show filtered count when filtering, otherwise show pagination */}
        {isAppointmentFiltered ? (
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: '10px' }}>
            <Typography>
              {t('TablePaginationFiltered', { count: data.length })}
            </Typography>
          </div>
        ) : totalPages > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
            <Typography>
              {t('TablePaginationInfo', {
                start: paginationMeta ? paginationMeta.offset + 1 : (currentPage * rowsPerPage) + 1,
                end: paginationMeta 
                  ? Math.min(paginationMeta.offset + paginationMeta.limit, paginationMeta.total_count)
                  : Math.min((currentPage + 1) * rowsPerPage, effectiveTotalCount),
                total: effectiveTotalCount
              })}
            </Typography>
            <Pagination
              sx={{
                width: 'auto',
                '& .MuiPaginationItem-root': {
                  width: '40px',
                  height: '32px',
                  fontSize: '14px',
                  borderRadius: '8px', 
                },
                '& .MuiPaginationItem-root.Mui-selected': {
                  backgroundColor: 'rgba(119, 119, 119, 1)',
                  color: 'white',
                  borderRadius: '8px',
                },
              }}
              count={totalPages} 
              page={page + 1}
              onChange={handleChangePage}
              siblingCount={2}
              boundaryCount={1}
              shape="rounded"
            />
          </div>
        )}
        {/* Confirmation Modal */}
        <ConfirmationModal
          open={openModal}
          handleClose={handleCloseModal}
          onConfirm={handleConfirmDelete}
          type="delete"
          title={t('Delete')}
          contentMessage={t('AreYouSureDeleteThisInformation')}
        />
      </div>
    </ThemeProvider>
  );
};

export default EditableTable;